import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isShapeLockedForUserEdit } from '@/lib/groups/element-edit-policy'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import type { TransparencyShapeTarget } from './types/transparency-shape-target'

/**
 * When a group shell is selected, transparency must apply to every unlocked
 * leaf descendant (shapes + nested text). The shell itself is visually
 * transparent, so updating only the group row has no visible effect.
 *
 * Set `includeGroupShell` when live-updating the control display for a selected
 * group. Persist/undo batches should omit shells so opacity-only changes do not
 * go through the geometric group reposition path.
 */
export function expandTransparencyShapeTargets(
  selectedShapes: ShapeData[],
  allShapes: ShapeData[],
  options?: { includeGroupShell?: boolean }
): TransparencyShapeTarget[] {
  const includeGroupShell = options?.includeGroupShell === true
  const byId = new Map(allShapes.map((shape) => [shape.id, shape]))
  const seen = new Set<string>()
  const targets: TransparencyShapeTarget[] = []

  const push = (shape: ShapeData) => {
    if (seen.has(shape.id)) return
    seen.add(shape.id)
    targets.push({
      id: shape.id,
      locked: isShapeLockedForUserEdit(shape, byId),
      opacity: shape.opacity,
    })
  }

  for (const selected of selectedShapes) {
    const current = byId.get(selected.id) ?? selected

    if (isGroupShape(current)) {
      if (includeGroupShell) {
        push(current)
      }

      for (const shape of allShapes) {
        if (shape.id === current.id) continue
        if (!isDescendantOfGroup(shape, current.id, byId)) continue
        // Nested group shells get opacity from their own selection path only.
        if (isGroupShape(shape)) continue
        push(shape)
      }

      continue
    }

    push(current)
  }

  return targets
}
